import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import {
  EnhancedAppointment,
  AppointmentStatus,
  BuildJsonType,
} from '@bb/domain/dist/types';
import { ILocationService, IRootScopeService, route } from 'angular';
import errorReporter from '@app/errorReporter';
import { TimePicker } from '../../react-common/Pickers';

const StyledButton = styled.button`
  margin: 0 5px 10px 5px;
`;

const StyledLink = styled.a`
  margin: 0 5px 10px 5px;
`;

const StyledWrapper = styled.div`
  max-width: 500px;
  margin: auto;
  h5 {
    margin-top: 25px;
    border-bottom: 1px solid #6a6c6f;
    margin: 25px 5px 0 5px;
    padding-bottom: 5px;
    text-transform: uppercase;
    text-align: center;
  }
  select,
  textarea {
    margin: 5px 5px 0 5px;
  }
`;

const StyledAnotherTimeWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const SWAL_STATUS_MAP: Record<string, string> = {
  confirm: 'Confirmed',
  cancel: 'Cancelled',
  deny: 'Denied',
};

type ExternalAuth = {
  auth: {
    dashboardId?: string;
    retsId?: string;
    externalDashboard?: string;
    coopDashboard?: string;
    retsDashboard?: string;
    user?: {
      id?: string;
    };
  };
};

interface ExternalStatusChangeProps {
  ExternalAuth: ExternalAuth;
  ExternalService: any;
  ToastService: any;
  $location: ILocationService;
  $route: route.IRouteService;
  $rootScope: IRootScopeService;
  status: AppointmentStatus;
  appointment: BuildJsonType<EnhancedAppointment>;
  clearSelected: () => void;
}

const ExternalStatusChange: React.FC<ExternalStatusChangeProps> = ({
  ExternalAuth: {
    auth: { coopDashboard, dashboardId, externalDashboard, user },
  },
  ExternalService,
  ToastService,
  $location,
  $route,
  $rootScope,
  status: parentStatus,
  appointment,
  clearSelected,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [status, setStatus] = React.useState<string>(parentStatus);
  const [permissions, setPermissions] = React.useState<any>({});

  const [details, setDetails] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [selectedTimeOption, setSelectedTimeOption] = React.useState('');
  const [selectedTime, setSelectedTime] = React.useState(moment());

  const backLinkRoute = appointment.buyers?.includes(user.id)
    ? `/dash/${dashboardId}/buyer_contact_appointments`
    : `/dash/${dashboardId}/appointments/listing/${appointment.listing.id}`;

  const setExternalStatus = async (newStatus = status as string) => {
    setLoading(true);
    try {
      await ExternalService.saveDashboardStatus({
        status: newStatus,
        dashboardId,
        details,
        appointmentId: appointment.id,
      });
      Swal.fire({
        title: `Showing ${SWAL_STATUS_MAP[newStatus]}!`,
        type: 'success',
      }).then(() => {
        $location.search({});
        $location.path(backLinkRoute);
        $route.reload();
      });
    } catch (err) {
      setLoading(false);
      errorReporter.error(err);
    }
  };

  const changeReason = ({
    target: { value },
  }: React.ChangeEvent<HTMLSelectElement>) => {
    let newTimeOption;
    let newTime;

    switch (value) {
      case 'anotherTime':
        newTimeOption = 'after';
        newTime = moment();
        setSelectedTimeOption(newTimeOption);
        setSelectedTime(newTime);
        setDetails(
          `Please book for ${newTimeOption} ${moment(newTime).format(
            'h:mm a',
          )}`,
        );
        break;
      case 'anotherDay':
        setDetails('Please book for another day');
        break;
      case 'reschedule':
        setDetails('Will reschedule showing');
        break;
      case 'notInterested':
        setDetails('Client no longer interested');
        break;
      default:
        setDetails('');
        break;
    }
    setReason(value);
  };

  React.useEffect(() => {
    const init = async () => {
      setLoading(true);

      let currentStatus = status as string;

      if (coopDashboard && appointment.status !== 'cancel') {
        currentStatus = 'cancel';
      }

      const currentPermissions = await ExternalService.getDashboardPermissions(
        dashboardId,
        appointment.id,
      );

      setPermissions(currentPermissions);

      setLoading(false);

      if (currentStatus === 'autoconfirm' && currentPermissions.confirm) {
        currentStatus = 'confirm';
        setExternalStatus(currentStatus);
        return;
      }

      if (currentStatus && !currentPermissions[currentStatus]) {
        if (currentStatus === 'autoconfirm') currentStatus = 'confirm';

        const alreadySet = currentStatus === appointment.status;
        $location.search({});
        $location.path(
          alreadySet
            ? `/dash/${dashboardId}/appointments/${appointment.id}`
            : backLinkRoute,
        );
        const message = alreadySet
          ? `This showing has already been ${SWAL_STATUS_MAP[
              currentStatus
            ].toLowerCase()}`
          : `You do not have access to ${currentStatus}`;
        $rootScope.$apply(() => {
          ToastService.add({
            message,
            type: 'warning',
          });
        });
      } else {
        setStatus(currentStatus);
      }
    };
    init();
  }, []);

  return (
    <StyledWrapper>
      {!status && (
        <div className="appointment-change-buttons-center">
          {loading && <StyledIcon className="fa fa-spinner fa-spin" />}
          {permissions.confirm && (
            <StyledLink
              className="btn btn-success"
              onClick={() => setExternalStatus('confirm')}
              aria-role="button"
              tabIndex={0}
            >
              Confirm
            </StyledLink>
          )}
          {permissions.deny && (
            <StyledLink
              className="btn btn-danger"
              onClick={() => setStatus('deny')}
              aria-role="button"
              tabIndex={0}
            >
              Deny
            </StyledLink>
          )}
          {permissions.cancel && (
            <StyledLink
              className="btn btn-danger"
              onClick={() => setStatus('cancel')}
              aria-role="button"
              tabIndex={0}
            >
              Cancel Showing
            </StyledLink>
          )}
          {permissions.time_change && (
            <StyledLink
              className="btn"
              style={{
                backgroundColor: '#4287f5',
                borderColor: '#4287f5',
                color: '#FFFFFF',
              }}
              href={`/external/#/dash/${dashboardId}/appointments/${appointment.id}/reschedule_contact`}
            >
              Suggest Better Time
            </StyledLink>
          )}
          {!loading && externalDashboard && (
            <StyledLink
              className="btn btn-default"
              onClick={clearSelected}
              href={`/external/#${backLinkRoute}`}
            >
              {appointment.status === 'suggested_time_change' ? 'Done' : 'Back'}
            </StyledLink>
          )}
        </div>
      )}
      {(status === 'cancel' || status === 'deny') && (
        <div>
          <h5>Are you sure you want to {status} this showing?</h5>
          {!coopDashboard ? (
            <select
              required
              className="form-control"
              value={reason}
              onChange={changeReason}
            >
              <option value="" disabled hidden>
                - SELECT REASON -
              </option>
              <option value="anotherTime">Book For Another Time</option>
              <option value="anotherDay">Book For Another Day</option>
              <option value="other">Other (Custom)</option>
            </select>
          ) : (
            status === 'cancel' && (
              <select
                required
                className="form-control"
                value={reason}
                onChange={changeReason}
                data-cy="select-reason"
              >
                <option value="" disabled hidden>
                  - SELECT REASON -
                </option>
                <option value="reschedule">I Will Reschedule</option>
                <option value="notInterested">
                  Client is No Longer Interested
                </option>
                <option value="other">Other (Custom)</option>
              </select>
            )
          )}
        </div>
      )}
      {reason === 'anotherTime' && (
        <div className="form-horizontal">
          <StyledAnotherTimeWrapper className="form-group">
            <div className="col-xs-4 col-xs-offset-1 col-sm-offset-2 col-sm-3">
              <div className="radio">
                <label style={{ marginTop: 20 }}>
                  <input
                    type="radio"
                    name="timeOption"
                    value="after"
                    checked={selectedTimeOption === 'after'}
                    onChange={({ target: { value } }) => {
                      setSelectedTimeOption(value);
                      setDetails(
                        `Please book for ${value} ${moment(selectedTime).format(
                          'h:mm a',
                        )}`,
                      );
                    }}
                  />
                  After:
                </label>
              </div>
              <div className="radio">
                <label style={{ margin: 0 }}>
                  <input
                    type="radio"
                    name="timeOption"
                    value="before"
                    checked={selectedTimeOption === 'before'}
                    onChange={({ target: { value } }) => {
                      setSelectedTimeOption(value);
                      setDetails(
                        `Please book for ${value} ${moment(selectedTime).format(
                          'h:mm a',
                        )}`,
                      );
                    }}
                  />
                  Before:
                </label>
              </div>
            </div>
            <div className="col-xs-7">
              <TimePicker
                inputVariant="outlined"
                value={selectedTime}
                onChange={newTime => {
                  setSelectedTime(newTime);
                  setDetails(
                    `Please book for ${selectedTimeOption} ${moment(
                      newTime,
                    ).format('h:mm a')}`,
                  );
                }}
                format="h:mm a"
                variant="inline"
                autoOk
              />
            </div>
          </StyledAnotherTimeWrapper>
        </div>
      )}
      {reason === 'other' && (
        <div>
          <textarea
            className="form-control"
            placeholder={`Why are you ${
              status === 'cancel' ? 'cancelling' : 'denying'
            } this showing?`}
            value={details}
            onChange={({ target: { value } }) => setDetails(value)}
            data-cy="textarea-custom-reason"
          ></textarea>
        </div>
      )}
      <br />
      {status && (
        <div
          className="appointment-change-buttons-center"
          style={{ justifyContent: 'flex-start' }}
        >
          <StyledButton
            className={`btn ${
              status === 'confirm'
                ? 'btn-success'
                : (status === 'deny' || status === 'cancel') && 'btn-danger'
            }`}
            disabled={loading || (!details && status !== 'confirm')}
            onClick={() => setExternalStatus(status)}
            style={{ marginRight: 10 }}
            data-cy="confirm-change-status"
          >
            {loading ? (
              <i className="fa fa-spinner fa-spin" />
            ) : (
              <span>
                {status.replace(/(^| )(\w)/g, x => x.toUpperCase())}{' '}
                {status !== 'confirm' ? 'Showing' : ''}
              </span>
            )}
          </StyledButton>
          {externalDashboard && (
            <StyledLink
              className="btn btn-default"
              onClick={clearSelected}
              href={`/external/#${backLinkRoute}`}
            >
              Back
            </StyledLink>
          )}
        </div>
      )}
    </StyledWrapper>
  );
};

const StyledIcon = styled.i`
  height: 12px;
  margin: 12px;
`;

export default ExternalStatusChange;
